import type {
  GenerateRoofVolumeOptions,
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { RoofConnectivity, RoofExtensions } from "building-types/settings/roofTypes";
import { GableRoofType, RoofBoundaryType } from "building-types/settings/settingsTypesEnum";
import { assertIsNotNullOrUndefined, assertIsNumber } from "common/utils/assertOfType";
import { roofExtensionsNew, MULTIGABLE_BSP_EPSILON, roofConnectivityNew } from "doc/utils/roofTools";
import { vec3PlaneBSPSide } from "geom/bsp";
import { DIST_EPSILON } from "geom/core";
import { planeNewXY, PlaneSide } from "geom/plane";
import { planeBSPByThreePoints } from "geom/planeBSP";
import { polyLine2ExtendEnd, polyLine2ExtendStart, polyLine2Length } from "geom/polyLine2";
import { polyLine3StartPoint, polyLine3EndPoint } from "geom/polyLine3";
import { touchesBuilding } from "../../roofs/roofNeighbors";
import {
  getExtensionAtHeightFromBase,
  getExtensions,
  getPeakExtension,
  isValidExtension,
} from "../../roofs/roofSlopes";

/**
 * Generates the roof extensions to connect the roof to the neighbor roofs.
 */
export function generateRoofVolumeExtensions({
  roofVolumeOptions,
  roofVolume,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const { baseHeight, baseToPeak, roofType, leftSideSlopes, rightSideSlopes } = roofVolume;

  const { neighborRoofs } = roofVolumeOptions;

  const { startNeighbor, endNeighbor } = neighborRoofs;
  const {
    slopes: startNeighborSlopes,
    baseHeight: startRoofBaseHeight,
    relativeDist: relativeStartDist,
  } = startNeighbor;
  const { slopes: endNeighborSlopes, baseHeight: endRoofBaseHeight, relativeDist: relativeEndDist } = endNeighbor;

  const startRelativeHeight = baseHeight - startRoofBaseHeight;
  const endRelativeHeight = baseHeight - endRoofBaseHeight;

  const peakHeight = baseHeight + baseToPeak;
  const basePlane = planeNewXY(0, 0, baseHeight);

  // the extension on each end of the gable is determined from the neighbor
  // roof slopes and the peak height of this gable
  const allowPopUp = roofType === GableRoofType.DEFAULT;
  const startLeftExtensions = getExtensions(
    leftSideSlopes,
    startNeighborSlopes,
    allowPopUp,
    relativeStartDist,
    startRelativeHeight,
  );

  const startRightExtensions = getExtensions(
    rightSideSlopes,
    startNeighborSlopes,
    allowPopUp,
    relativeStartDist,
    startRelativeHeight,
  );

  const endLeftExtensions = getExtensions(
    leftSideSlopes,
    endNeighborSlopes,
    allowPopUp,
    relativeEndDist,
    endRelativeHeight,
  );

  const endRightExtensions = getExtensions(
    rightSideSlopes,
    endNeighborSlopes,
    allowPopUp,
    relativeEndDist,
    endRelativeHeight,
  );

  // we should have an extension value for each slope
  if (!(startLeftExtensions.length === leftSideSlopes.length)) {
    console.assert(false);
  }
  if (!(startRightExtensions.length === rightSideSlopes.length)) {
    console.assert(false);
  }
  if (!(endLeftExtensions.length === leftSideSlopes.length)) {
    console.assert(false);
  }
  if (!(endRightExtensions.length === rightSideSlopes.length)) {
    console.assert(false);
  }

  const hasStartExtension = isValidExtension(startLeftExtensions) || isValidExtension(startRightExtensions);
  const hasEndExtension = isValidExtension(endLeftExtensions) || isValidExtension(endRightExtensions);

  const extensions = roofExtensionsNew({
    hasStartExtension,
    hasEndExtension,
    startLeftExtensions,
    startRightExtensions,
    endLeftExtensions,
    endRightExtensions,
  });

  // prepare roof ends
  const { connectivity: startConnectivity, baseExtension: startBaseExtension } = prepareRoofEnd(
    roofVolumeOptions,
    roofVolume,
    extensions,
    peakHeight,
    false,
  );

  const { connectivity: endConnectivity, baseExtension: endBaseExtension } = prepareRoofEnd(
    roofVolumeOptions,
    roofVolume,
    extensions,
    peakHeight,
    true,
  );

  return {
    roofVolume: {
      basePlane,
      peakHeight,
      extensions: {
        ...extensions,
        hasStartPartialOverlap: startConnectivity.hasPartialOverlap,
        hasEndPartialOverlap: endConnectivity.hasPartialOverlap,
      },
      startConnectivity,
      endConnectivity,
    },
    temporaries: {
      startBaseExtension,
      endBaseExtension,
    },
  };
}

interface PrepareRoofEndResult {
  connectivity: RoofConnectivity;
  baseExtension: number;
}

function prepareRoofEnd(
  roofVolumeOptions: GenerateRoofVolumeOptions,
  roofVolume: Partial<GeneratedRoofVolumeLegacy>,
  extensions: RoofExtensions,
  peakHeight: number,
  endSide: boolean,
): PrepareRoofEndResult {
  const { baseHeight, roofType, leftSideSlopes, rightSideSlopes, heelHeightOffsets, rails2D, rectangularOverhangs } =
    roofVolume;

  assertIsNotNullOrUndefined(rectangularOverhangs);
  const { startOverhang, endOverhang, leftOverhang, rightOverhang, baseStartOverhang, baseEndOverhang } =
    rectangularOverhangs;

  const {
    hasStartExtension,
    hasEndExtension,
    startLeftExtensions,
    startRightExtensions,
    endLeftExtensions,
    endRightExtensions,
  } = extensions;

  assertIsNotNullOrUndefined(rails2D);
  const { leftRail, rightRail, peakRail } = rails2D;
  const { clipContext, neighborRoofs, insertedRoofVolumes, roofInputSettings } = roofVolumeOptions;
  const { roofSettings } = roofInputSettings;
  const { startNeighbor, endNeighbor } = neighborRoofs;
  const neighbor = endSide ? endNeighbor : startNeighbor;
  const leftExtensions = endSide ? endLeftExtensions : startLeftExtensions;
  const rightExtensions = endSide ? endRightExtensions : startRightExtensions;

  const {
    slopes: neighborSlopes,
    baseHeight: neighborBaseHeight,
    baseHeightOffset: neighborBaseHeightOffset,
    overhang: neighborRoofOverhang,
    relativeDist,
  } = neighbor;

  const neighborRoofVolume = insertedRoofVolumes[neighbor.roofId];

  const neighborLeftRail = neighborRoofVolume?.rails?.leftRail;
  const neighborRightRail = neighborRoofVolume?.rails?.rightRail;
  const neighborClipToPerimeter = neighborRoofVolume?.boundaryType === RoofBoundaryType.CLIP_TO_PERIMETER;

  const { insideBSP } = clipContext;
  const { boundaryType = RoofBoundaryType.BOUNDING_BOX } = roofSettings;

  const extendFunc = endSide ? polyLine2ExtendEnd : polyLine2ExtendStart;

  let hasPartialOverlap = false;
  let baseExtension = 0;
  const hasExtension = endSide ? hasEndExtension : hasStartExtension;
  if (hasExtension) {
    const endSideOverhang = endSide ? endOverhang : startOverhang;
    const endSideBaseOverhang = endSide ? baseEndOverhang : baseStartOverhang;

    assertIsNumber(baseHeight);
    const leftHeightDiff =
      baseHeight + (heelHeightOffsets?.leftOffset ?? 0) - (neighborBaseHeight + neighborBaseHeightOffset);
    const rightHeightDiff =
      baseHeight + (heelHeightOffsets?.rightOffset ?? 0) - (neighborBaseHeight + neighborBaseHeightOffset);

    const [l0x, l0y, l0z] = polyLine3StartPoint(neighborLeftRail);
    const [l1x, l1y, l1z] = polyLine3EndPoint(neighborLeftRail);
    const [r0x, r0y, r0z] = polyLine3StartPoint(neighborRightRail);
    const [r1x, r1y, r1z] = polyLine3EndPoint(neighborRightRail);

    const leftPlane = planeBSPByThreePoints(
      leftRail[2],
      leftRail[3],
      0,
      leftRail[0],
      leftRail[1],
      0,
      leftRail[0],
      leftRail[1],
      1,
    );
    const rightPlane = planeBSPByThreePoints(
      rightRail[0],
      rightRail[1],
      0,
      rightRail[2],
      rightRail[3],
      0,
      rightRail[2],
      rightRail[3],
      1,
    );

    let isLeftOutside = false;
    if (boundaryType === RoofBoundaryType.CLIP_TO_PERIMETER && roofType !== GableRoofType.RIGHT_SIDE_SHED_ROOF) {
      assertIsNotNullOrUndefined(leftPlane);
      if (
        vec3PlaneBSPSide(l0x, l0y, l0z, leftPlane, MULTIGABLE_BSP_EPSILON) === PlaneSide.BACK &&
        vec3PlaneBSPSide(l1x, l1y, l1z, leftPlane, MULTIGABLE_BSP_EPSILON) === PlaneSide.BACK &&
        vec3PlaneBSPSide(r0x, r0y, r0z, leftPlane, MULTIGABLE_BSP_EPSILON) === PlaneSide.BACK &&
        vec3PlaneBSPSide(r1x, r1y, r1z, leftPlane, MULTIGABLE_BSP_EPSILON) === PlaneSide.BACK
      ) {
        isLeftOutside = true;
      }
    }

    let isRightOutside = false;
    if (boundaryType === RoofBoundaryType.CLIP_TO_PERIMETER && roofType !== GableRoofType.LEFT_SIDE_SHED_ROOF) {
      assertIsNotNullOrUndefined(rightPlane);
      if (
        vec3PlaneBSPSide(l0x, l0y, l0z, rightPlane, MULTIGABLE_BSP_EPSILON) === PlaneSide.BACK &&
        vec3PlaneBSPSide(l1x, l1y, l1z, rightPlane, MULTIGABLE_BSP_EPSILON) === PlaneSide.BACK &&
        vec3PlaneBSPSide(r0x, r0y, r0z, rightPlane, MULTIGABLE_BSP_EPSILON) === PlaneSide.BACK &&
        vec3PlaneBSPSide(r1x, r1y, r1z, rightPlane, MULTIGABLE_BSP_EPSILON) === PlaneSide.BACK
      ) {
        isRightOutside = true;
      }
    }

    const leftExtension = isLeftOutside
      ? 0
      : getExtensionAtHeightFromBase(neighborSlopes, leftHeightDiff + neighborBaseHeightOffset) - relativeDist;

    const rightExtension = isRightOutside
      ? 0
      : getExtensionAtHeightFromBase(neighborSlopes, rightHeightDiff + neighborBaseHeightOffset) - relativeDist;

    const leftBaseExtension =
      leftHeightDiff >= -DIST_EPSILON ? leftExtension - endSideOverhang - neighborRoofOverhang : -endSideOverhang;
    const rightBaseExtension =
      rightHeightDiff >= -DIST_EPSILON ? rightExtension - endSideOverhang - neighborRoofOverhang : -endSideOverhang;

    assertIsNotNullOrUndefined(leftSideSlopes);
    let hasSomeLeftOverlapping = false;
    for (let i = 0, l = leftExtensions.length; i < l; i++) {
      const { hasStartOverlapping, hasEndOverlapping } = leftSideSlopes[i];
      const isOverlapping = endSide ? hasEndOverlapping : hasStartOverlapping;
      if (isOverlapping) {
        // special case when the left side is collinear or inside the parent roof
        if (!isLeftOutside && i === 0) {
          const relativeHeight = baseHeight - neighborBaseHeight;
          const endHeight = leftSideSlopes[i].endHeight + relativeHeight;
          leftExtensions[i] = getExtensionAtHeightFromBase(neighborSlopes, endHeight) - relativeDist;
        }

        leftExtensions[i] -= leftExtension;
        hasSomeLeftOverlapping = true;
      } else {
        if (!hasSomeLeftOverlapping) {
          leftExtensions[i] = 0;
        }
        hasPartialOverlap = true;
      }

      if (neighborClipToPerimeter) {
        leftExtensions[i] -= endSideOverhang;
      }
    }

    assertIsNotNullOrUndefined(rightSideSlopes);
    let hasSomeRightOverlapping = false;
    for (let i = 0, l = rightExtensions.length; i < l; i++) {
      const { hasStartOverlapping, hasEndOverlapping } = rightSideSlopes[i];
      const isOverlapping = endSide ? hasEndOverlapping : hasStartOverlapping;
      if (isOverlapping) {
        // special case when the right side is collinear or inside the parent roof
        if (!isRightOutside && i === 0) {
          const relativeHeight = baseHeight - neighborBaseHeight;
          const endHeight = rightSideSlopes[i].endHeight + relativeHeight;
          rightExtensions[i] = getExtensionAtHeightFromBase(neighborSlopes, endHeight) - relativeDist;
        }

        rightExtensions[i] -= rightExtension;
        hasSomeRightOverlapping = true;
      } else {
        if (!hasSomeRightOverlapping) {
          rightExtensions[i] = 0;
        }
        hasPartialOverlap = true;
      }

      if (neighborClipToPerimeter) {
        rightExtensions[i] -= endSideOverhang;
      }
    }

    if (neighborClipToPerimeter) {
      extendFunc(leftRail, endSideOverhang);
      extendFunc(rightRail, endSideOverhang);
    }

    const { hasStartOverlapping: hasStartLeftOverlapping, hasEndOverlapping: hasEndLeftOverlapping } =
      leftSideSlopes[0];
    const isLeftOverlapping = endSide ? hasEndLeftOverlapping : hasStartLeftOverlapping;
    if (isLeftOverlapping) {
      extendFunc(leftRail, Math.max(leftBaseExtension, -polyLine2Length(leftRail) + 0.1));
    }

    const { hasStartOverlapping: hasRightLeftOverlapping, hasEndOverlapping: hasEndRightOverlapping } =
      rightSideSlopes[0];
    const isRightOverlapping = endSide ? hasEndRightOverlapping : hasRightLeftOverlapping;
    if (isRightOverlapping) {
      extendFunc(rightRail, Math.max(rightBaseExtension, -polyLine2Length(rightRail) + 0.1));
    }

    baseExtension = Math.max(leftBaseExtension, rightBaseExtension) + endSideBaseOverhang;

    const minBaseExtension = Math.min(leftBaseExtension, rightBaseExtension);
    extendFunc(peakRail, Math.max(minBaseExtension, -polyLine2Length(peakRail) + 0.1));
  }

  const peakExtension = getPeakExtension(leftExtensions);
  extendFunc(peakRail, peakExtension);

  assertIsNumber(baseHeight);
  const isTouchingBuilding = touchesBuilding({
    node: insideBSP,
    leftRail: roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF ? peakRail : leftRail,
    rightRail: roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? peakRail : rightRail,
    peakRail,
    baseHeight,
    peakHeight,
    leftOverhang: leftOverhang,
    rightOverhang: rightOverhang,
    endSide,
    peakOffsetZ: roofType === GableRoofType.DEFAULT ? -1 : 0,
  });

  const isFree = (hasPartialOverlap || !hasExtension) && !isTouchingBuilding;

  return {
    connectivity: roofConnectivityNew({ isFree, isTouchingBuilding, hasPartialOverlap }),
    baseExtension,
  };
}
